import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Plus, Trash2, ChevronDown, ChevronUp, Users, Check, Baby } from 'lucide-react';

const defaultGuest = {
  name: '', email: '', phone: '', side: '', relation: '', plusOne: false, plusOneName: '',
  hasChildren: false, childrenCount: 0, childrenAges: '',
  rsvp: 'pending', meal: 'standard', allergies: '', dietaryNotes: '',
  accessibility: '', transportNeed: false, hotelNeed: false, notes: '',
};

const GuestRow = ({ guest, index, onUpdate, onRemove, expanded, onToggle, sideLabels }) => {
  const update = (field, value) => onUpdate(index, { ...guest, [field]: value });
  const rsvpColors = { confirmed: 'bg-green-100 text-green-700', declined: 'bg-red-100 text-red-700', pending: 'bg-stone-100 text-stone-500' };

  return (
    <div className={`border rounded-xl transition-all ${expanded ? 'border-primary/30 bg-white' : 'border-stone-200 bg-stone-50/50'}`}>
      <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={onToggle}>
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-sans font-bold text-primary">
          {guest.name ? guest.name[0].toUpperCase() : (index + 1)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-sans font-medium text-stone-800 truncate">{guest.name || `Guest ${index + 1}`}</p>
          <div className="flex items-center gap-2">
            {guest.side && <span className="text-[10px] font-sans text-stone-400">{sideLabels[guest.side] || guest.side}</span>}
            <span className={`text-[10px] font-sans font-bold px-1.5 py-0.5 rounded-full ${rsvpColors[guest.rsvp] || rsvpColors.pending}`}>
              {guest.rsvp === 'confirmed' ? 'Confirmed' : guest.rsvp === 'declined' ? 'Declined' : 'Pending'}
            </span>
            {guest.hasChildren && <Baby className="w-3 h-3 text-stone-400" />}
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); onRemove(index); }}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
        {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-stone-100 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Field label="Name *" value={guest.name} onChange={v => update('name', v)} placeholder="Full name" />
            <Field label="Email" value={guest.email} onChange={v => update('email', v)} placeholder="e-post" type="email" />
            <Field label="Phone" value={guest.phone} onChange={v => update('phone', v)} placeholder="Phone" />
            <Select label="Side" value={guest.side} onChange={v => update('side', v)} options={[
              { value: 'person1', label: sideLabels.person1 || 'Person 1s side' }, { value: 'person2', label: sideLabels.person2 || 'Person 2s side' }, { value: 'both', label: 'Felles' },
            ]} />
            <Select label="Relasjon" value={guest.relation} onChange={v => update('relation', v)} options={[
              { value: 'family', label: 'Familie' }, { value: 'friend', label: 'Venn' },
              { value: 'colleague', label: 'Kollega' }, { value: 'other', label: 'Other' },
            ]} />
            <Select label="RSVP" value={guest.rsvp} onChange={v => update('rsvp', v)} options={[
              { value: 'pending', label: 'Pending' }, { value: 'confirmed', label: 'Confirmed' }, { value: 'declined', label: 'Declined' },
            ]} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Select label="Meal choice" value={guest.meal} onChange={v => update('meal', v)} options={[
              { value: 'standard', label: 'Standard' }, { value: 'vegetarian', label: 'Vegetarian' },
              { value: 'vegan', label: 'Vegan' }, { value: 'glutenfree', label: 'Gluten-free' },
              { value: 'halal', label: 'Halal' }, { value: 'kosher', label: 'Kosher' }, { value: 'other', label: 'Other' },
            ]} />
            <Field label="Allrgyer / intoleranser" value={guest.allergies} onChange={v => update('allergies', v)} placeholder="e.g. nuts, lactose" />
            <Field label="Availablehetsbehov" value={guest.accessibility} onChange={v => update('accessibility', v)} placeholder="e.g. rullestol" />
          </div>
          <div className="flex flex-wrap gap-3">
            <Toggle label="Partner / +1" checked={guest.plusOne} onChange={v => update('plusOne', v)} />
            <Toggle label="Har barn" checked={guest.hasChildren} onChange={v => update('hasChildren', v)} />
            <Toggle label="Transportbehov" checked={guest.transportNeed} onChange={v => update('transportNeed', v)} />
            <Toggle label="Hotellbehov" checked={guest.hotelNeed} onChange={v => update('hotelNeed', v)} />
          </div>
          {guest.plusOne && (
            <Field label="Partneres navn" value={guest.plusOneName} onChange={v => update('plusOneName', v)} placeholder="Partnerens navn" />
          )}
          {guest.hasChildren && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Number of barn" value={guest.childrenCount} onChange={v => update('childrenCount', v)} type="number" placeholder="0" />
              <Field label="Childas alder" value={guest.childrenAges} onChange={v => update('childrenAges', v)} placeholder="e.g. 3, 7" />
            </div>
          )}
          <Field label="Notes" value={guest.notes} onChange={v => update('notes', v)} placeholder="Andre merknader" />
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="text-[10px] font-sans font-semibold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-[10px] font-sans font-semibold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none appearance-none">
      <option value="">Select...</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <div className={`w-8 h-5 rounded-full transition-all flex items-center ${checked ? 'bg-primary' : 'bg-stone-200'}`}
      onClick={() => onChange(!checked)}>
      <div className={`w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </div>
    <span className="text-xs font-sans text-stone-600">{label}</span>
  </label>
);

export const GuestListBuilder = ({ guests, onChange, couple }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);

  const sideLabels = useMemo(() => {
    if (!couple) return { person1: 'Person 1s side', person2: 'Person 2s side', both: 'Felles' };
    const p1 = couple.person1Name || 'Person 1';
    const p2 = couple.person2Name || 'Person 2';
    return { person1: `${p1}s side`, person2: `${p2}s side`, both: 'Felles' };
  }, [couple]);

  const addGuest = () => {
    onChange([...guests, { ...defaultGuest }]);
    setExpandedIdx(guests.length);
  };

  const removeGuest = (idx) => onChange(guests.filter((_, i) => i !== idx));
  const updateGuest = (idx, updated) => onChange(guests.map((g, i) => i === idx ? updated : g));

  const stats = useMemo(() => {
    const confirmed = guests.filter(g => g.rsvp === 'confirmed').length;
    const declined = guests.filter(g => g.rsvp === 'declined').length;
    const pending = guests.filter(g => g.rsvp === 'pending').length;
    const children = guests.reduce((sum, g) => sum + (g.hasChildren ? (parseInt(g.childrenCount) || 1) : 0), 0);
    const dietary = guests.filter(g => g.meal !== 'standard' && g.meal).length;
    const accessibility = guests.filter(g => g.accessibility).length;
    return { confirmed, declined, pending, children, dietary, accessibility, total: guests.length };
  }, [guests]);

  return (
    <div data-testid="guest-list-builder" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Guest list</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Add guests with all details</p>
          <p className="text-[10px] font-sans text-stone-400 mt-1 leading-relaxed">
            RSVP, dietary needs, allergies and logistics are registered per guest and used by seating, children, transport and budget modules.
          </p>
        </div>
        <Button type="button" onClick={addGuest} variant="outline" size="sm" className="rounded-full text-xs font-sans gap-1.5 border-stone-200">
          <Plus className="w-3.5 h-3.5" /> Add guest
        </Button>
      </div>

      {/* Stats */}
      {guests.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          <Stat label="Totalt" value={stats.total} />
          <Stat label="Confirmed" value={stats.confirmed} color="text-green-600" />
          <Stat label="Venter" value={stats.pending} color="text-amber-600" />
          <Stat label="Declined" value={stats.declined} color="text-red-500" />
          <Stat label="Child" value={stats.children} />
          <Stat label="Spesialkost" value={stats.dietary} />
        </div>
      )}

      <div className="space-y-2">
        {guests.map((guest, i) => (
          <GuestRow key={i} guest={guest} index={i} onUpdate={updateGuest} onRemove={removeGuest}
            expanded={expandedIdx === i} onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)} sideLabels={sideLabels} />
        ))}
      </div>

      {guests.length === 0 && (
        <button type="button" onClick={addGuest}
          className="w-full py-8 border-2 border-dashed border-stone-200 rounded-2xl hover:border-primary/40 transition-colors text-center">
          <Users className="w-7 h-7 text-stone-300 mx-auto mb-2" />
          <p className="text-sm font-sans text-stone-500">Click to add your first guest</p>
        </button>
      )}
    </div>
  );
};

const Stat = ({ label, value, color = 'text-stone-700' }) => (
  <div className="bg-white rounded-xl border border-stone-100 p-2.5 text-center">
    <p className={`text-lg font-sans font-bold ${color}`}>{value}</p>
    <p className="text-[10px] font-sans text-stone-400 uppercase tracking-wide">{label}</p>
  </div>
);
